/**
 * When each part of the lab map's scroll sequence happens, as shares of scroll progress `p` from 0
 * to 1. The table, the steps, the progress bar and the 3D scene all read these, and this file
 * imports nothing, so the page follows the scroll before the 3D code has loaded, or without it.
 *
 * There are three formulas. The first runs in its own clock `q`, the share of the first stage done
 * (`firstOf`): the scan reads each row and its ingredient prints on the label, the physician signs,
 * and only then do the pills go in, protein last, and the pouch is sealed. Mixing before the
 * signature would show the opposite of the promise.
 *
 * The second and third, at three months and at six, each run in a clock `r` (`laterOf`). The
 * pouch in front steps back, the scan reads the new results row by row, a new pouch rises into the
 * front, its label prints the adjusted doses, and the physician signs again.
 */
const clamp = (x: number) => Math.min(1, Math.max(0, x));

export const span = (p: number, from: number, length: number) => clamp((p - from) / length);

export const smooth = (x: number) => x * x * (3 - 2 * x);

export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const SCAN_START = 0.02;

const SCAN_STEP = 0.03;

export const POP = 0.02;

/** Below the side-by-side width the pouch's scene replaces the table's once the scan is done. */
const RISE_AT = 0.29;

const SIGN_AT = 0.31;

const SIGN = 0.06;

export const OPEN_AT = 0.37;

const POWDER_FIRST = 0.41;

const POWDER_GAP = 0.04;

export const FLIGHT = 0.07;

export const CRUMBLE = 0.02;

export const FALL = 0.06;

export const BESIDE_AT = 0.6;

export const SCOOP_AT = 0.66;

export const SCOOP_FLIGHT = 0.08;

export const TIP_AT = 0.74;

export const POUR_AT = 0.76;

export const POUR = 0.08;

export const SEAL_AT = 0.88;

/** Where the first stage ends, as a share of the whole scroll. */
const FIRST_END = 0.48;

/** Where the three-month and six-month stages start, and how long each runs. */
const LATER = [
  { at: 0.5, length: 0.23 },
  { at: 0.75, length: 0.25 },
] as const;

/** In a later stage's clock: the scan of the new results, then the new pouch. */
const RESCAN_START = 0.1;

const RESCAN_STEP = 0.03;

const ARRIVE_AT = 0.42;

/*
  A later formula's step starts once its pouch is in front, not as it starts to rise. Starting with
  the rise put the second formula's step over the first pouch.
*/
const FORMULA_AT = 0.52;

const REPRINT_AT = 0.52;

const RESIGN_AT = 0.66;

/* How long a switch between the table's scene and the pouches' takes. Long fades left both half
   drawn, table text ghosting under the pouch. */
const SWITCH = 0.015;

/** Where each month's stage is complete: its formula signed and in front. A still screen rests here, and the month buttons scroll here. */
export const MONTH_ENDS: readonly number[] = [FIRST_END, LATER[0].at + LATER[0].length, 1];

export const firstOf = (p: number) => clamp(p / FIRST_END);

/**
 * Where the progress bar rests: 0 on the first month's dot, 0.5 on three months, 1 on six. It is
 * always on a dot. A stage's start sends it to the next one, and the bar's own transition does the
 * travel, so a position in the scroll never leaves it between two.
 */
export function barAt(p: number): number {
  const [three, six] = LATER;

  if (p >= six.at) return 1;

  return p >= three.at ? 0.5 : 0;
}

/** The clock of a later stage: 0 for the formula at three months, 1 for the one at six. */
export function laterOf(stage: 0 | 1, p: number): number {
  const { at, length } = LATER[stage];

  return span(p, at, length);
}

export type Phase =
  | "reading"
  | "signing"
  | "blending"
  | "scooping"
  | "sealing"
  | "labs-3"
  | "formula-3"
  | "labs-6"
  | "formula-6";

function firstPhase(q: number): Phase {
  if (q < SIGN_AT - 0.01) return "reading";

  if (q < POWDER_FIRST - 0.02) return "signing";

  if (q < SCOOP_AT - 0.01) return "blending";

  return q < SEAL_AT ? "scooping" : "sealing";
}

export function phaseAt(p: number): Phase {
  if (p >= LATER[1].at) return laterOf(1, p) < FORMULA_AT ? "labs-6" : "formula-6";

  if (p >= LATER[0].at) return laterOf(0, p) < FORMULA_AT ? "labs-3" : "formula-3";

  return firstPhase(firstOf(p));
}

/** The numbered step each phase belongs to, 0 to 7. Scooping and sealing share one. */
export const STEP_OF: Record<Phase, number> = {
  reading: 0,
  signing: 1,
  blending: 2,
  scooping: 3,
  sealing: 3,
  "labs-3": 4,
  "formula-3": 5,
  "labs-6": 6,
  "formula-6": 7,
};

/** Where each step starts, as a share of the whole scroll, then 1 for the end of the last. */
export const STEP_BOUNDS: readonly number[] = [
  0,
  (SIGN_AT - 0.01) * FIRST_END,
  (POWDER_FIRST - 0.02) * FIRST_END,
  (SCOOP_AT - 0.01) * FIRST_END,
  LATER[0].at,
  LATER[0].at + FORMULA_AT * LATER[0].length,
  LATER[1].at,
  LATER[1].at + FORMULA_AT * LATER[1].length,
  1,
];

/** When, in the first stage's clock, the scan reaches a row. */
export function rowAt(row: number): number {
  return SCAN_START + row * SCAN_STEP;
}

/** When, in a later stage's clock, the scan reaches a row of the new results. */
export function rescanAt(row: number): number {
  return RESCAN_START + row * RESCAN_STEP;
}

/** Which results a row shows at `p`: 0 the first labs, 1 at three months, 2 at six. */
export function resultAt(row: number, p: number): 0 | 1 | 2 {
  if (laterOf(1, p) >= rescanAt(row)) return 2;

  return laterOf(0, p) >= rescanAt(row) ? 1 : 0;
}

/** The formula the stage is about at `p`: 0 the first, 1 at three months, 2 at six. */
export function formulaAt(p: number): 0 | 1 | 2 {
  if (p >= LATER[1].at) return 2;

  return p >= LATER[0].at ? 1 : 0;
}

/**
 * Below the side-by-side width the stage holds one scene at a time: the table while results are
 * read, the pouches while a formula is signed and made. 0 is the table, 1 the pouches.
 */
export function sceneAt(p: number): number {
  const stage = formulaAt(p);

  if (stage === 0) return smooth(span(firstOf(p), RISE_AT, SWITCH * 2));

  const r = laterOf(stage === 1 ? 0 : 1, p);

  return 1 - smooth(span(r, 0.04, SWITCH)) + smooth(span(r, FORMULA_AT - SWITCH * 2, SWITCH));
}

/** Where pouch `v` is: how many places it has stepped back, and how far below the stage it is. */
export interface Placing {
  readonly depth: number;
  readonly below: number;
}

export function pouchAt(v: 0 | 1 | 2, p: number): Placing {
  const back = (stage: 0 | 1) => smooth(span(laterOf(stage, p), 0, 0.1));
  const rise = (stage: 0 | 1) => 1 - smooth(span(laterOf(stage, p), ARRIVE_AT, 0.1));

  if (v === 0) return { depth: back(0) + back(1), below: 0 };

  if (v === 1) return { depth: back(1), below: rise(0) };

  return { depth: 0, below: rise(1) };
}

/** How much of the first signature has drawn, in the first stage's clock. */
export function signedAt(q: number): number {
  return span(q, SIGN_AT, SIGN);
}

/** When the `line`th line of a later label prints, in that stage's clock. */
export function reprintAt(line: number): number {
  return REPRINT_AT + line * 0.018;
}

/** How much of a later signature has drawn, in that stage's clock. */
export function resignedAt(r: number): number {
  return span(r, RESIGN_AT, 0.12);
}

/** When the `order`th powdered pill, counted in table order, starts its flight to the pouch. */
export function powderAt(order: number): number {
  return POWDER_FIRST + order * POWDER_GAP;
}

/** When, in the first stage's clock, a pill has left its cell, so the cell can show its dose. */
export function leftAt(fate: "powder" | "beside" | "scoop", order: number): number {
  if (fate === "powder") return powderAt(order) + FLIGHT * 0.3;

  if (fate === "beside") return BESIDE_AT + 0.03;

  return SCOOP_AT + 0.03;
}
